import { GetterTree } from 'vuex';

// Shared types
import { Accessory, System, RequestOfferRequest } from '@coldtec/cos-types';

// Store types
import { RootState, RequestGetter } from '@/store/store.types';

/**
 *  Holding every getter of the request.
 */
export const Getters: GetterTree<RootState, RootState> = {

    /**
     *  Used to get the valid state of the request.
     */
    [RequestGetter.VALID](state: RootState): boolean {

        return state.request.valid;

    },

    /**
     *  Used to get the full request state.
     */
    [RequestGetter.REQUEST](state: RootState): RequestOfferRequest {

        return state.request.data;

    },

    /**
     *  Used to get the count of the spaces that are added to the request.
     */
    [RequestGetter.COUNT_SPACES](state: RootState): number {

        return state.request.data.spaces.length;

    },

    /**
     *  Used to get the count of the systems that are selected for the request.
     */
    [RequestGetter.COUNT_SYSTEMS](state: RootState): number {

        let count = 0;

        for (const space of state.request.data.spaces) {

            if (!space.system) {

                continue;

            }

            count++;

        }

        return count;

    },

    /**
     *  Used to get the count of products that are added to the request.
     */
    [RequestGetter.COUNT_PRODUCTS](state: RootState): number {

        return state.request.data.service_products.length + state.request.data.additional_products.length;

    },

    /**
     *  Used to get the total price of the request.
     */
    [RequestGetter.PRICE_TOTAL](state: RootState): number {

        let total = 0;

        /**
         *
         *  Map for keeping track of which systems
         *  are already added to the price.
         *
         *  (i) A multi split system is added to all
         *  spaces, but only needs to be counted once
         *
         */
        const counted: { [systemGuid: string]: boolean } = {};

        /**
         *  Add the prices of every system and their added accessories.
         */
        for (const space of state.request.data.spaces) {

            if (!space.system) {

                continue;

            }

            /**
             *  Get the data of the system to add his (and his accessories') price.
             */
            const system: System = state.systems.suggestions.systems[space.system];

            /**
             *
             *  Only try to add the prices of the accessories if both
             *  the system and space have an accessory Array.
             *
             *  (i) The space's Array holds the guids of the accessories, the Array
             *  of the system holds the actual data of the accessories
             *
             */
            if (Array.isArray(system.accessories) && Array.isArray(space.accessories)) {

                /**
                 *  Find the accessory that got added and add his price
                 *  to the total price for the accessories.
                 */
                for (const accessoryGuid of space.accessories) {

                    for (const accessory of system.accessories as Array<Accessory>) {

                        if (accessory.guid !== accessoryGuid) {

                            continue;

                        }

                        total += accessory.price || 0;

                        break;

                    }

                }

            }

            /**
             *  Do not count the price of the system again if already counted.
             */
            if (counted[space.system]) {

                continue;

            }

            /**
             *  Add the price of the system.
             */
            total += system.price || 0;

            /**
             *  Add the system guid to the counted Array
             *  to prevent if from being counted again.
             */
            counted[space.system] = true;

        }

        /**
         *  Add the prices of every added service product.
         */
        for (const serviceProductGuid of state.request.data.service_products) {

            total += state.products.map.service[serviceProductGuid].price || 0;

        }

        /**
         *
         *  Add the prices of every added additional product.
         *
         *  (i) The price of an additional product is computed
         *  and updated in his `price` property
         *
         */
        for (const additionalProduct of state.request.data.additional_products) {

            total += additionalProduct.price || 0;

        }

        return total;

    },


};
